/**
 * CapabilityProvider — step-up authorization (May 2026 port).
 *
 * Subscribes the singleton ``WSClient`` to the typed ``capabilities_changed``
 * event and invalidates the TanStack Query cache when it fires. The provider
 * doesn't own React state of its own — the manifest lives in the query cache
 * (key: ``capabilityManifestQueryKey``) so any descendant can read it via
 * ``useCapabilityManifest()`` / ``useCapability(name)`` / ``useHasCapability``
 * without prop-drilling.
 *
 * Ports v1 ``ando-frontend/app/components/contexts/CapabilityContext.tsx``
 * with these differences:
 *   - Uses TanStack Query for cache + revalidate-on-focus (v2 already pins
 *     react-query; v1 didn't, hence the bespoke provider).
 *   - WS invalidation goes through ``WSClient.onMessage("capabilities_changed")``
 *     instead of v1's ``capabilitiesChangedEmitter`` EventTarget.
 *   - Tab-visibility refresh is owned by TanStack Query ``refetchOnWindowFocus``.
 *
 * Mounted in ``_authed/route.tsx`` next to ``WSProvider`` so the auth cookie
 * is settled when the first ``fetchCapabilityManifest()`` fires.
 */

import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useWsClient } from "@/features/api";

import { capabilityManifestQueryKey } from "./useCapabilityManifest.js";

/**
 * Query keys that depend on the capability manifest. When a connector is
 * added/removed the BE pushes ``capabilities_changed`` and we invalidate
 * each of these in one pass — SOTA May 2026 pattern (TanStack Query
 * docs: event-driven invalidation over polling).
 *
 * Keep the prefixes terse — TanStack Query treats them as "matches
 * anything starting with X", so any sub-key (filters etc.) is covered.
 */
const CAPABILITY_DEPENDENT_QUERY_PREFIXES: ReadonlyArray<readonly unknown[]> = [
  [...capabilityManifestQueryKey],
  ["drive-folders"], // useDriveFolders — connector add/remove changes the folder list
  ["chat-suggestions"], // useQuickPrompts — workspace prompts depend on active connectors
];

export function CapabilityProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const ws = useWsClient();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    // Re-fetch every capability-derived query whenever the BE pushes
    // ``capabilities_changed``. Payload (changed_capabilities) is
    // informational — we always re-pull the full state so cross-feature
    // caches (manifest + drive folders + quick prompts + …) stay in sync.
    return ws.onMessage("capabilities_changed", () => {
      for (const queryKey of CAPABILITY_DEPENDENT_QUERY_PREFIXES) {
        void queryClient.invalidateQueries({ queryKey });
      }
    });
  }, [ws, queryClient]);

  return <>{children}</>;
}
